import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Input } from "@/shared/components/ui/input";
import {
  Play,
  Square,
  RefreshCw,
  MessagesSquare,
  ChevronRight,
  ChevronDown,
  ArrowRightToLine,
} from "lucide-react";
import { toast } from "sonner";
import { LogTerminal, LogTerminalRef } from "@/shared/components/ui/log-terminal";
import { getAgenticChats, type ChatFeedItem } from "../services/serviceLogsService";

// Anonymized prompt+response feed (Agentic tab → "Chats" mode, PBI 81485).
// Layout: session sidebar (grouped by sessionKey, newest activity first) +
// conversation feed for the selected session (or all). Live mode polls the feed
// on a serialized timer; a date narrows to one day. Identity arrives already
// anonymized from the API (stable hashes + PII masking).

const POLL_MS = 5000;
const FEED_TAIL = 200;

const CHANNEL_BADGES: Record<ChatFeedItem["channel"], { label: string; className: string }> = {
  playground: { label: "Playground", className: "text-emerald-400 bg-emerald-400/15" },
  communicator: { label: "Communicator", className: "text-violet-400 bg-violet-400/15" },
  mcp: { label: "MCP", className: "text-amber-400 bg-amber-400/15" },
};

// Prose do markdown da resposta — espelho reduzido do agentProse do Playground
// (ChatBubbleV2), theme-aware (o LogTerminal alterna claro/escuro).
const RESPONSE_PROSE = [
  "prose prose-sm prose-neutral dark:prose-invert max-w-none break-words",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
  "prose-p:my-1.5 prose-li:my-0.5",
  "prose-headings:font-semibold",
  "prose-pre:bg-gray-200/70 dark:prose-pre:bg-black/40 prose-pre:rounded-md",
  "prose-code:before:content-none prose-code:after:content-none",
  "prose-a:text-sky-600 dark:prose-a:text-sky-400 prose-a:no-underline hover:prose-a:underline",
  "prose-th:border prose-th:border-gray-300 dark:prose-th:border-white/15 prose-th:px-2 prose-th:py-1",
  "prose-td:border prose-td:border-gray-300 dark:prose-td:border-white/15 prose-td:px-2 prose-td:py-1",
].join(" ");

// Bolhas estilo Playground, theme-aware, com fonte sans (o terminal é mono).
const USER_BUBBLE =
  "max-w-[85%] rounded-2xl rounded-br-md bg-[hsl(var(--primary))] px-3 py-2 text-sm text-white font-sans whitespace-pre-wrap break-words";
const RESPONSE_BUBBLE =
  "max-w-[85%] rounded-2xl rounded-bl-md bg-muted px-3 py-2 text-sm text-foreground font-sans";

// Pares curtos já abrem expandidos; longos abrem contraídos (preview de uma linha).
const AUTO_EXPAND_MAX_CHARS = 280;

const formatTime = (iso: string): string => {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString();
};

// Compact range: same day → "8/11/2026, 10:01 → 10:37"; cross-day shows both dates.
const formatRange = (firstIso: string, lastIso: string): string => {
  const first = new Date(firstIso);
  const last = new Date(lastIso);
  if (Number.isNaN(first.getTime()) || Number.isNaN(last.getTime())) return formatTime(lastIso);
  if (first.getTime() === last.getTime()) return last.toLocaleString();
  const timeOpts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit" };
  if (first.toDateString() === last.toDateString()) {
    return `${first.toLocaleDateString()}, ${first.toLocaleTimeString([], timeOpts)} → ${last.toLocaleTimeString([], timeOpts)}`;
  }
  return `${first.toLocaleString()} → ${last.toLocaleString()}`;
};

interface SessionGroup {
  sessionKey: string;
  channel: ChatFeedItem["channel"];
  organizationName: string | null;
  productName: string | null;
  userHash: string;
  firstAt: string;
  lastAt: string;
  count: number;
}

// Groups feed items (newest first) into session blocks ordered by latest activity.
const groupSessions = (items: ChatFeedItem[]): SessionGroup[] => {
  const groups = new Map<string, SessionGroup>();
  for (const item of items) {
    const existing = groups.get(item.sessionKey);
    if (existing) {
      existing.count += 1;
      // items chegam do mais novo para o mais antigo — o último visto é o firstAt
      existing.firstAt = item.promptAt;
    } else {
      groups.set(item.sessionKey, {
        sessionKey: item.sessionKey,
        channel: item.channel,
        organizationName: item.organizationName,
        productName: item.productName,
        userHash: item.userHash,
        firstAt: item.promptAt,
        lastAt: item.promptAt,
        count: 1,
      });
    }
  }
  return [...groups.values()];
};

export const AgenticChatsFeed = () => {
  const { t } = useTranslation();
  const [items, setItems] = useState<ChatFeedItem[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [date, setDate] = useState<string>(""); // '' = live
  const [selectedSession, setSelectedSession] = useState<string | null>(null); // null = all
  // Overrides de expansão por item: pares curtos abrem expandidos, longos contraídos;
  // um clique no chevron inverte o default daquele item.
  const [toggledIds, setToggledIds] = useState<Set<string>>(new Set());
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
  const [isPaused, setIsPaused] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  // Live-poll health: silent failures must not leave a green "live" badge while
  // the data is actually stale — surfaced as a non-intrusive indicator.
  const [pollFailed, setPollFailed] = useState(false);
  const logTerminalRef = useRef<LogTerminalRef>(null);
  const isPausedRef = useRef(isPaused);
  // Monotonic request sequence: a slow older response must not overwrite the
  // state written by a newer one (e.g. day switch racing the live poll).
  const requestSeqRef = useRef(0);

  const isLive = date === "";

  useEffect(() => {
    isPausedRef.current = isPaused;
  }, [isPaused]);

  const load = useCallback(
    async (targetDate: string, silent = false) => {
      const requestSeq = ++requestSeqRef.current;
      if (!silent) setIsLoading(true);
      try {
        const response = await getAgenticChats({
          date: targetDate || null,
          tail: FEED_TAIL,
        });
        // Every state update derived from this request stays behind the sequence
        // guard — a stale success must not clear a newer failure (and vice versa).
        if (requestSeq === requestSeqRef.current) {
          setItems(response.items ?? []);
          setPollFailed(false);
        }
      } catch {
        if (requestSeq === requestSeqRef.current) setPollFailed(true);
        if (!silent) toast.error(t("logs.chats.loadError", "Could not load the conversation feed."));
      } finally {
        if (!silent) setIsLoading(false);
      }
    },
    [t]
  );

  // Live mode: serialized recursive setTimeout (never overlapping requests).
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const tick = async () => {
      if (cancelled) return;
      if (!isPausedRef.current) await load("", true);
      if (!cancelled) timer = setTimeout(tick, POLL_MS);
    };

    // finally (não then): um erro no load inicial não pode matar o polling —
    // o load já trata o erro; aqui só garantimos que o loop continue.
    void load(date).finally(() => {
      if (isLive && !cancelled) timer = setTimeout(tick, POLL_MS);
    });

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [date, isLive, load]);

  // Reset session selection when the day changes (session may not exist there).
  useEffect(() => {
    setSelectedSession(null);
    setToggledIds(new Set());
    setExpandedGroups(new Set());
  }, [date]);

  const toggleGroup = (sessionKey: string) => {
    setExpandedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(sessionKey)) next.delete(sessionKey);
      else next.add(sessionKey);
      return next;
    });
  };

  const autoExpanded = (item: ChatFeedItem) =>
    item.prompt.length + (item.response?.length ?? 0) <= AUTO_EXPAND_MAX_CHARS;

  const isItemExpanded = (item: ChatFeedItem) =>
    toggledIds.has(item.id) ? !autoExpanded(item) : autoExpanded(item);

  const toggleItem = (id: string) => {
    setToggledIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const sessions = useMemo(() => groupSessions(items), [items]);

  // Sessão selecionada = visão de conversa corrida (cronológica, como o Playground);
  // "Todas as sessões" = lista compacta (mais novo primeiro) com expansão por par.
  const isConversationView = selectedSession !== null;

  const filteredItems = useMemo(() => {
    let result = items;
    if (selectedSession) {
      result = result.filter((item) => item.sessionKey === selectedSession);
    }
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (item) =>
          item.prompt.toLowerCase().includes(query) ||
          (item.response ?? "").toLowerCase().includes(query) ||
          (item.organizationName ?? "").toLowerCase().includes(query) ||
          (item.productName ?? "").toLowerCase().includes(query) ||
          item.userHash.toLowerCase().includes(query) ||
          item.channel.toLowerCase().includes(query)
      );
    }
    return result;
  }, [items, selectedSession, searchQuery]);

  // Conversa corrida: ordem cronológica (o feed chega do mais novo para o mais antigo).
  const conversationItems = useMemo(
    () => (isConversationView ? [...filteredItems].reverse() : filteredItems),
    [isConversationView, filteredItems]
  );

  // "Todas as sessões": agrupa os pares por sessão (grupos pela atividade mais
  // recente; trocas de cada grupo em ordem cronológica).
  const groupedFeed = useMemo(() => {
    if (isConversationView) return [];
    const map = new Map<string, ChatFeedItem[]>();
    for (const item of filteredItems) {
      const arr = map.get(item.sessionKey) ?? [];
      arr.push(item);
      map.set(item.sessionKey, arr);
    }
    return [...map.entries()].map(([sessionKey, groupItems]) => ({
      sessionKey,
      items: [...groupItems].reverse(),
    }));
  }, [isConversationView, filteredItems]);

  // Abrir uma conversa é navegação explícita: força o scroll mesmo que o usuário
  // tenha rolado para cima na visão anterior (senão a conversa abre no meio).
  useEffect(() => {
    if (!isConversationView) return;
    const timeoutId = setTimeout(() => logTerminalRef.current?.forceScrollToBottom(), 100);
    return () => clearTimeout(timeoutId);
  }, [isConversationView, selectedSession]);

  // Mensagens novas na conversa aberta acompanham o fim (como um chat) apenas se
  // o usuário não tiver rolado para cima para ler algo.
  useEffect(() => {
    if (!isConversationView) return;
    const timeoutId = setTimeout(() => logTerminalRef.current?.scrollToBottom(), 100);
    return () => clearTimeout(timeoutId);
  }, [isConversationView, conversationItems.length]);

  const renderSessionBlock = (session: SessionGroup) => {
    const badge = CHANNEL_BADGES[session.channel];
    const isSelected = selectedSession === session.sessionKey;
    return (
      <button
        key={session.sessionKey}
        onClick={() => setSelectedSession(isSelected ? null : session.sessionKey)}
        className={`w-full text-left rounded-md border p-2 space-y-1 transition-colors ${
          isSelected
            ? "border-primary bg-primary/10"
            : "border-border hover:bg-muted/60"
        }`}
      >
        <div className="flex items-center justify-between gap-2">
          <span
            className={`${badge.className} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
          >
            {badge.label}
          </span>
          <span className="text-[10px] text-muted-foreground whitespace-nowrap">
            {session.count} {t("logs.chats.messages", "msgs")}
          </span>
        </div>
        {session.organizationName && (
          <div className="text-xs font-medium truncate">{session.organizationName}</div>
        )}
        {session.productName && (
          <div className="text-xs text-muted-foreground truncate">{session.productName}</div>
        )}
        <div className="text-[10px] text-muted-foreground font-mono truncate">
          {session.userHash}
        </div>
        <div className="text-[10px] text-muted-foreground">
          {formatRange(session.firstAt, session.lastAt)}
        </div>
      </button>
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3 flex-wrap">
        <Input
          placeholder={t("logs.chats.searchPlaceholder", "Buscar nas conversas...")}
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="flex-1 max-w-md min-w-[180px]"
        />
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground whitespace-nowrap">
            {t("logs.dateSelector", "Data")}:
          </span>
          <input
            type="date"
            value={date}
            onChange={(e) => setDate(e.target.value)}
            className="h-9 text-sm px-3 rounded-md border border-input bg-background"
          />
          {!isLive && (
            <button
              onClick={() => setDate("")}
              className="text-sm text-primary hover:underline"
            >
              {t("logs.liveOption", "Tempo real (hoje)")}
            </button>
          )}
        </div>
        <span className="text-sm">
          {isLive ? (
            isPaused ? (
              <span className="flex items-center text-yellow-500">
                <span className="h-2 w-2 rounded-full bg-yellow-500 mr-2"></span>
                {t("logs.paused", "Pausado")}
              </span>
            ) : pollFailed ? (
              <span className="flex items-center text-red-500">
                <span className="h-2 w-2 rounded-full bg-red-500 mr-2"></span>
                {t("logs.liveUpdatesFailing", "Tempo real com falhas — dados podem estar desatualizados")}
              </span>
            ) : (
              <span className="flex items-center text-green-500">
                <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse mr-2"></span>
                {t("logs.chats.liveFeed", "Feed em tempo real")}
              </span>
            )
          ) : (
            <span className="flex items-center text-blue-400">
              <span className="h-2 w-2 rounded-full bg-blue-400 mr-2"></span>
              {t("logs.historicalSnapshot", "Snapshot histórico — sem atualização em tempo real")}
            </span>
          )}
        </span>
        {filteredItems.length > 0 && (
          <span className="text-sm text-muted-foreground">
            {t("logs.chats.itemCount", { count: filteredItems.length })}
          </span>
        )}
      </div>

      <div className="flex gap-4 h-[500px]">
        {/* Session sidebar */}
        <div className="w-60 shrink-0 flex flex-col border rounded-md bg-card overflow-hidden">
          <div className="px-3 py-2 border-b flex items-center gap-2 text-sm font-medium">
            <MessagesSquare className="h-4 w-4" />
            {t("logs.chats.sessions", "Sessões")}
            <span className="ml-auto text-xs text-muted-foreground">{sessions.length}</span>
          </div>
          <div className="flex-1 overflow-y-auto p-2 space-y-2">
            <button
              onClick={() => setSelectedSession(null)}
              className={`w-full text-left rounded-md border p-2 text-sm transition-colors ${
                selectedSession === null
                  ? "border-primary bg-primary/10 font-medium"
                  : "border-border hover:bg-muted/60"
              }`}
            >
              {t("logs.chats.allSessions", "Todas as sessões")}
            </button>
            {sessions.map(renderSessionBlock)}
            {sessions.length === 0 && !isLoading && (
              <p className="text-xs text-muted-foreground px-1 py-2">
                {t("logs.chats.noSessions", "Nenhuma sessão no período.")}
              </p>
            )}
          </div>
        </div>

        {/* Conversation feed */}
        <div className="flex-1 overflow-hidden">
          {filteredItems.length === 0 && !isLoading ? (
            <div className="flex flex-col items-center justify-center h-full border rounded-md bg-card text-muted-foreground">
              <p>{t("logs.chats.empty", "Nenhuma conversa no período selecionado.")}</p>
            </div>
          ) : (
            <LogTerminal
              className="h-full"
              ref={logTerminalRef}
              title={t("logs.chats.terminalTitle", "Conversas anonimizadas (Agentic)")}
              actionButtons={
                <div className="flex space-x-3">
                  {isLive &&
                    (isPaused ? (
                      <button
                        onClick={() => setIsPaused(false)}
                        className="text-green-500 hover:text-green-700 focus:outline-none"
                        title={t("logs.resume", "Retomar")}
                      >
                        <Play className="h-4 w-4" />
                      </button>
                    ) : (
                      <button
                        onClick={() => setIsPaused(true)}
                        className="text-yellow-500 hover:text-yellow-700 focus:outline-none"
                        title={t("logs.pause", "Pausar")}
                      >
                        <Square className="h-4 w-4" />
                      </button>
                    ))}
                  <button
                    onClick={() => load(date)}
                    className="text-gray-500 hover:text-gray-700 focus:outline-none"
                    title={t("logs.chats.refresh", "Atualizar")}
                  >
                    <RefreshCw className={`h-4 w-4 ${isLoading ? "animate-spin" : ""}`} />
                  </button>
                </div>
              }
            >
              {isConversationView
                ? // Conversa corrida (sessão selecionada) — o chat inteiro, como no Playground.
                  conversationItems.map((item) => (
                    <div key={item.id} className="py-1.5">
                      <div className="text-center text-[10px] text-gray-500 my-1">
                        {formatTime(item.promptAt)}
                      </div>
                      <div className="flex justify-end">
                        <div className={USER_BUBBLE}>{item.prompt}</div>
                      </div>
                      {item.response !== null ? (
                        <div className="mt-2 flex justify-start">
                          <div className={RESPONSE_BUBBLE}>
                            <ReactMarkdown remarkPlugins={[remarkGfm]} className={RESPONSE_PROSE}>
                              {item.response}
                            </ReactMarkdown>
                          </div>
                        </div>
                      ) : (
                        <div className="mt-2 text-gray-500 italic text-sm font-sans">
                          {t("logs.chats.noResponse", "Sem resposta registrada")}
                        </div>
                      )}
                    </div>
                  ))
                : // "Todas as sessões" — grupos por sessão; trocas em estilo log dentro do grupo.
                  groupedFeed.map((group) => {
                    const head = group.items[group.items.length - 1]; // mais recente
                    const first = group.items[0];
                    const badge = CHANNEL_BADGES[head.channel];
                    return (
                      <div
                        key={group.sessionKey}
                        className="py-2 border-b border-hairline last:border-b-0"
                      >
                        {/* Cabeçalho da sessão — clique expande/retrai as trocas inline;
                            o botão à direita abre a conversa completa */}
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => toggleGroup(group.sessionKey)}
                            className="flex items-center gap-2 flex-wrap text-xs flex-1 min-w-0 text-left hover:bg-gray-200/40 dark:hover:bg-white/5 rounded px-1 py-0.5"
                            title={
                              expandedGroups.has(group.sessionKey)
                                ? t("logs.chats.collapse", "Contrair")
                                : t("logs.chats.expand", "Expandir")
                            }
                          >
                            <span className="text-gray-500 shrink-0">
                              {expandedGroups.has(group.sessionKey) ? (
                                <ChevronDown className="h-3.5 w-3.5" />
                              ) : (
                                <ChevronRight className="h-3.5 w-3.5" />
                              )}
                            </span>
                            <span
                              className={`${badge.className} font-mono text-[10px] font-medium px-1.5 py-0.5 rounded`}
                            >
                              {badge.label}
                            </span>
                            {head.organizationName && (
                              <span className="text-sky-600 dark:text-sky-400">
                                {head.organizationName}
                              </span>
                            )}
                            {head.productName && (
                              <span className="text-teal-600 dark:text-teal-400">
                                {head.productName}
                              </span>
                            )}
                            <span className="text-gray-500 font-mono">{head.userHash}</span>
                            <span className="text-gray-500 font-mono text-[10px]">
                              {group.sessionKey}
                            </span>
                            <span className="ml-auto text-gray-500 text-[10px] whitespace-nowrap">
                              {formatRange(first.promptAt, head.promptAt)} · {group.items.length}{" "}
                              {t("logs.chats.messages", "msgs")}
                            </span>
                          </button>
                          <button
                            onClick={() => setSelectedSession(group.sessionKey)}
                            className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none shrink-0 px-1"
                            title={t("logs.chats.openSession", "Abrir conversa")}
                          >
                            <ArrowRightToLine className="h-3.5 w-3.5" />
                          </button>
                        </div>

                        {/* Trocas da sessão em ordem cronológica, estilo log */}
                        <div
                          className={`mt-1 ml-5 border-l border-hairline pl-2 space-y-1 ${
                            expandedGroups.has(group.sessionKey) ? "" : "hidden"
                          }`}
                        >
                          {group.items.map((item) => {
                            const expanded = isItemExpanded(item);
                            return (
                              <div key={item.id} className="flex items-start gap-1">
                                <button
                                  onClick={() => toggleItem(item.id)}
                                  className="mt-0.5 text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 focus:outline-none shrink-0"
                                  title={
                                    expanded
                                      ? t("logs.chats.collapse", "Contrair")
                                      : t("logs.chats.expand", "Expandir")
                                  }
                                >
                                  {expanded ? (
                                    <ChevronDown className="h-3.5 w-3.5" />
                                  ) : (
                                    <ChevronRight className="h-3.5 w-3.5" />
                                  )}
                                </button>
                                {expanded ? (
                                  <div className="min-w-0 flex-1 border-l border-hairline pl-2 space-y-1">
                                    <div className="text-sky-700 dark:text-sky-300/80 text-xs whitespace-pre-wrap break-words">
                                      <span className="text-gray-500 mr-2">
                                        [{formatTime(item.promptAt)}] ❯
                                      </span>
                                      {item.prompt}
                                    </div>
                                    <div className="text-muted-foreground text-xs whitespace-pre-wrap break-words">
                                      <span className="text-gray-500 mr-2">⏎</span>
                                      {item.response ??
                                        t("logs.chats.noResponse", "Sem resposta registrada")}
                                    </div>
                                  </div>
                                ) : (
                                  <button
                                    onClick={() => toggleItem(item.id)}
                                    className="min-w-0 flex-1 text-left"
                                  >
                                    <div className="text-sky-700 dark:text-sky-300/80 text-xs truncate">
                                      <span className="text-gray-500 mr-2">
                                        [{formatTime(item.promptAt)}] ❯
                                      </span>
                                      {item.prompt}
                                    </div>
                                    <div className="text-muted-foreground text-xs truncate">
                                      <span className="text-gray-500 mr-2">⏎</span>
                                      {item.response ??
                                        t("logs.chats.noResponse", "Sem resposta registrada")}
                                    </div>
                                  </button>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })}
            </LogTerminal>
          )}
        </div>
      </div>
    </div>
  );
};
